import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Control, useController, useForm, UseFormWatch } from 'react-hook-form'
import {
  CreateCustomQualityControlDto,
  CustomQualityControlDto,
  CustomQualityControlType,
  HotLabEquipmentType,
} from '@services/dtos'
import { customQualityControlSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, IconButton, TextField, Tooltip } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import {
  createCustomQualityControl,
  updateCustomQualityControl,
} from '@state/reducers/customQualityControlReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField, { useInputFieldStyles } from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { CustomQualityControlTypeLabels, HotLabEquipmentTypeLabels } from '@utils/constants'
import { FormCrudHeader } from './FormCrudHeader'
import { DataGridPro, GridToolbarContainer } from '@mui/x-data-grid-pro'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

interface Option {
  id: string;
  label: string;
  code: string;
}

function OptionsToolbar({
  onAdd,
  onDelete,
  control,
  watch,
}: {
  onAdd: (option: { code: string; label: string }) => void
  onDelete: () => void
  control: Control<any>
  watch: UseFormWatch<any>
}) {
  const options = watch('options') ?? []
  const [open, setOpen] = useState(false)
  const [newOption, setNewOption] = useState({ code: '', label: '' })
  const dispatch = useAppDispatch()
  const classes = useInputFieldStyles()
  
  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setNewOption({ code: '', label: '' })
  }

  const handleValidate = () => {
    const trimmedCode = newOption.code.trim()
    const trimmedLabel = newOption.label.trim()
    if (!trimmedCode || !trimmedLabel) return

    const codeExists = options.some((opt) => opt.code === trimmedCode)
    const labelExists = options.some((opt) => opt.label === trimmedLabel)
    if (codeExists || labelExists) {
      dispatch(
        enqueueSnackbar({
          message: 'Le code ou le libellé existe déjà pour une autre option',
          options: { variant: 'success' },
        })
      )
      return
    }

    onAdd(newOption)
    handleClose()
  }

  return (
    <GridToolbarContainer
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button color="primary" variant="contained" onClick={handleOpen}>
          Ajouter option
        </Button>
        <Tooltip title="Supprimer option">
          <IconButton onClick={onDelete} color="primary">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {options.length > 0 && (
        <Box sx={{ minWidth: 250 }}>
          <SelectField
            control={control}
            name="expectedOptionCode"
            options={options
              .filter(({ label, code }) => label && code)
              .map(({ label, code }) => ({
                label: `${label} (${code})`,
                value: code,
              }))}
            value={watch('expectedOptionCode')}
            label="Option attendue"
          />
        </Box>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Ajouter une option</DialogTitle>
        <DialogContent className="flex flex-col gap-2 mt-2 !pt-2">
          <TextField
            label="Code"
            value={newOption.code}
            onChange={(e) => setNewOption((prev) => ({ ...prev, code: e.target.value }))}
            inputProps={{ maxLength: 50 }}
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Libellé"
            value={newOption.label}
            onChange={(e) => setNewOption((prev) => ({ ...prev, label: e.target.value }))}
            inputProps={{ maxLength: 120 }}
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleValidate}
            color="primary"
            variant="contained"
            disabled={!newOption.code.trim() || !newOption.label.trim()}
          >
            Valider
          </Button>
        </DialogActions>
      </Dialog>
    </GridToolbarContainer>
  )
}

export function ControleQualiteOptionsGrid({
  control,
  watch,
  error,
}: {
  control: Control<any>
  watch: UseFormWatch<any>
  error?: string
}) {
  const {
    field: { value: options = [], onChange },
  } = useController({
    name: 'options',
    control,
  })

  const [selectionModel, setSelectionModel] = React.useState<string[]>([])

  const columns = [
    { field: 'code', headerName: 'Code', width: 80, editable: true },
    { field: 'label', headerName: 'Libellé', flex: 1, editable: true },
  ]
  const handleAdd = (newOpt?: { code: string; label: string }) => {
    const newOption: Option = {
      id: uuidv4(),
      code: newOpt?.code ?? '',
      label: newOpt?.label ?? '',
    }
    onChange([...options, newOption])
  }
  const handleDelete = () => {
    const updated = options.filter((opt: Option) => !selectionModel.includes(opt.id))
    onChange(updated)
  }
  const handleProcessRowUpdate = (newRow: Option) => {
    const updated = options.map((r: Option) => (r.id === newRow.id ? newRow : r))
    onChange(updated)
    return newRow
  }

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      {error && <FormHelperText error>{error}</FormHelperText>}

      <DataGridPro
        rows={options}
        columns={columns}
        getRowId={(row) => row.id}
        checkboxSelection
        disableSelectionOnClick
        processRowUpdate={handleProcessRowUpdate}
        onSelectionModelChange={(ids) =>
          setSelectionModel(ids as string[])
        }
        components={{ Toolbar: OptionsToolbar }}
        componentsProps={{
          toolbar: {
            onAdd: handleAdd,
            onDelete: handleDelete,
            control,
            watch,
          },
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Box>
  )
}

const VISUAL_QUALITY_CONTROL_TYPE_OPTIONS = (
  Object.entries(CustomQualityControlTypeLabels) as Array<
    [CustomQualityControlType, string]
  >
).map(([value, label]) => ({ label, value }))

const EQUIPMENT_TYPE_OPTIONS = (
  Object.entries(HotLabEquipmentTypeLabels) as Array<[HotLabEquipmentType, string]>
).map(([value, label]) => ({ label, value }))

type Props = {
  editingCustomQualityControl?: CustomQualityControlDto | null
  onClose: () => void
}

const CustomQualityControlForm: React.FC<Props> = ({
  editingCustomQualityControl,
  onClose,
}) => {
  const { watch, handleSubmit, reset, control, setValue, clearErrors, formState: { errors } } =
    useForm<CreateCustomQualityControlDto>({
      defaultValues: editingCustomQualityControl
        ? {
            code: editingCustomQualityControl.code,
            label: editingCustomQualityControl.label,
            type: editingCustomQualityControl.type,
            unit: editingCustomQualityControl.unit,
            options: editingCustomQualityControl.options,
            expectedOptionCode:
              editingCustomQualityControl.expectedOption?.code,
            expected: editingCustomQualityControl.expected,
            expectedValue: editingCustomQualityControl.expectedValue,
            minimum: editingCustomQualityControl.minimum ?? undefined,
            maximum: editingCustomQualityControl.maximum ?? undefined,
            equipmentTypes: editingCustomQualityControl.equipmentTypes ?? [],
          }
        : {},
      resolver: yupResolver(customQualityControlSchema),
    })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  useEffect(() => {
    const options = watch('options') || []
    const expectedOptionCode = watch('expectedOptionCode')
    if (
      expectedOptionCode &&
      !options.find((opt) => opt.code === expectedOptionCode)
    ) {
      setValue('expectedOptionCode', undefined)
    }
  }, [watch('options')])

  const onSubmitForm = async (data: CreateCustomQualityControlDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () =>
      cb(
        'Une erreur est survenue, vérifiez que le code ou label n\'est pas déjà utilisé',
      )

    // Clean ids
    if (data.options) {
      data.options = data.options.map(({ code, label }) => ({ code, label }))
    }
    if (editingCustomQualityControl) {
      dispatch(
        updateCustomQualityControl({
          id: editingCustomQualityControl.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('Le contrôle qualité personnalisé a été modifié avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createCustomQualityControl(data))
        .unwrap()
        .then(() => {
          cb('Le contrôle qualité personnalisé a été créé avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const type = watch('type')

  useEffect(() => {
    const allFields = [
      'options',
      'expectedOptionCode',
      'expectedValue',
      'expected',
      'minimum',
      'maximum',
    ] as const
    const fieldsByType: Record<CustomQualityControlType, readonly (typeof allFields[number])[]> = {
      [CustomQualityControlType.BOOLEAN]: [
        'options',
        'expectedOptionCode',
        'expectedValue',
        'minimum',
        'maximum',
      ],
      [CustomQualityControlType.LIST]: [
        'expectedValue',
        'expected',
      ],
      [CustomQualityControlType.TEXT]: [
        'options',
        'expectedOptionCode',
        'expected',
        'minimum',
        'maximum',
      ],
      [CustomQualityControlType.NUMERIC]: [
        'options',
        'expectedOptionCode',
        'expected',
        'expectedValue',
      ],
    }
    const fieldsToClear = fieldsByType[type] ?? []
    fieldsToClear.forEach((field) => setValue(field, undefined))
    clearErrors(fieldsToClear)
  }, [type, setValue, clearErrors])

  const fields = [
    { name: 'code', label: 'Code' },
    { name: 'label', label: 'Libellé' },
    { name: 'unit', label: 'Unité' },
  ]
  const renderFields = (
    fields: { name: string; label: string; type?: string; sm?: number }[],
  ) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
        />
      </Grid>
    ))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingCustomQualityControl}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Contrôle qualité personnalisé
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {renderFields([fields[0], fields[1]])}

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={EQUIPMENT_TYPE_OPTIONS}
                  value={EQUIPMENT_TYPE_OPTIONS.filter((o) => (watch('equipmentTypes') ?? []).includes(o.value))}
                  getOptionLabel={(o) => o.label}
                  onChange={(_, selected) => setValue('equipmentTypes', selected.map((o) => o.value))}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.value} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Matériel de laboratoire" />
                  )}
                />
              </Grid>

              {renderFields([fields[1], fields[2]])}

              <Grid item xs={12} sm={6}>
                <SelectField
                  control={control}
                  name={'type'}
                  options={VISUAL_QUALITY_CONTROL_TYPE_OPTIONS}
                  value={watch('type')}
                  label={'Type'}
                />
              </Grid>

              {type === CustomQualityControlType.TEXT && (
                <Grid item xs={12}>
                  <InputField
                    control={control}
                    name="expectedValue"
                    label="Valeur attendue"
                    type="text"
                  />
                </Grid>
              )}

              {type === CustomQualityControlType.NUMERIC && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      control={control}
                      name="minimum"
                      label="Minimum"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      control={control}
                      name="maximum"
                      label="Maximum"
                      type="number"
                    />
                  </Grid>
                </>
              )}

              {type === CustomQualityControlType.BOOLEAN && (
                <Grid item xs={6}>
                  <SelectField
                    control={control}
                    name={'expected'}
                    onChange={(e) => setValue('expected', (e.target.value as string) === 'true')}
                    options={[
                      { label: 'Oui', value: 'true' },
                      { label: 'Non', value: 'false' },
                    ]}
                    value={watch('expected')}
                    label={'Valeur attendue'}
                  />
                </Grid>
              )}

              {type === CustomQualityControlType.LIST && (
                <Grid item xs={12}>
                  <ControleQualiteOptionsGrid
                    control={control}
                    watch={watch}
                    error={Array.isArray(errors.options)
                      ? 'Format invalide'
                      : (errors.options as any)?.message}
                  />
                </Grid>
              )}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default CustomQualityControlForm
